/* eslint-disable complexity */
import React, { FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { FormattedMessage } from '@futrli/localisation';

import { ConsolidatedForecastReport } from '../../core/types';
import { usePredictionsInfoContext } from '../../forecast/detail/context/predictions-info-context';
import { getAllNodesRefs } from '../../forecast/detail/predictions/util-fns/consolidation-util-fns';
import { UserGridSettingsContext } from '../../forecast/detail/reports/user-grid-settings/user-grid-settings-context';

import styles from './view-collapse-expand.scss';

interface Props {
  customExpandName?: string;
  customCollapseName?: string;
}

const isExpandAllParamPresent = (): boolean => {
  const params = new URLSearchParams(window.location.search);
  return params.has('expandAll') || window.location.pathname.includes('onboarding');
};

export const ViewCollapseExpand: FC<Props> = ({ customExpandName, customCollapseName }: Props) => {
  const { clearExpandedRows, toggleExpandedRows, expandedRows } = useContext(UserGridSettingsContext);
  const { consolidatedForecastReport, forecastReportV2, isConsolidation } = usePredictionsInfoContext();
  const [showExpand, setShowExpand] = useState(!expandedRows.length);

  const expandAllParam = useMemo(() => isExpandAllParamPresent(), []);

  useEffect(() => {
    setShowExpand(!expandedRows.length);
  }, [expandedRows]);

  const handleExpandAllV2 = useCallback(
    (forecastReport: ConsolidatedForecastReport | null | undefined) => {
      if (!forecastReport) {
        return;
      }
      clearExpandedRows();
      toggleExpandedRows(...getAllNodesRefs(forecastReport));
    },
    [clearExpandedRows, toggleExpandedRows]
  );

  const handleExpandAllRows = useCallback((): void => {
    handleExpandAllV2(isConsolidation ? consolidatedForecastReport : forecastReportV2);
  }, [consolidatedForecastReport, forecastReportV2, handleExpandAllV2, isConsolidation]);

  const handleCollapseAllRows = (): void => {
    clearExpandedRows();
  };

  useEffect(() => {
    if (expandAllParam) {
      handleExpandAllRows();
    }
  }, [expandAllParam, handleExpandAllRows]);

  return (
    <div>
      {showExpand && (
        <div className={styles.newButtonLink} onClick={handleExpandAllRows} data-testid="view-expand-all-rows">
          <span>{customExpandName || <FormattedMessage id="common.expand_all" defaultMessage="Expand all" />}</span>
        </div>
      )}
      {!showExpand && (
        <div className={styles.newButtonLink} onClick={handleCollapseAllRows} data-testid="view-collapse-all-rows">
          <span>
            {customCollapseName || <FormattedMessage id="common.collapse_all" defaultMessage="Collapse all" />}
          </span>
        </div>
      )}
    </div>
  );
};
